import Auth2 from 'plugin/src/shared/Auth2'
import {
  _persistBroadcastIdentify,
  _persistDataToStorage,
  broadcastLastLoginHostname,
  responseIncludesAuth,
  getAccessToken as webGetAccessToken,
  getDeviceId as webGetDeviceId,
  getDeviceIdOrAccessTokenHash as webGetDeviceIdOrAccessTokenHash
} from 'web/src/shared/Auth'
import { canAccessWebLocalStorage, isEmpty } from '../assertion'
import { WEB_STORAGE_PREFIX_KEYS } from '../constants'
import { DiagnosticsLogger } from '../diagnostics'
import { isInExtension } from '../extension'
import { reallyTruthy } from '../validation'
import { flattenDataFromResponse } from './flattenDataFromResponse'

const hasLocalStoragePermission = canAccessWebLocalStorage()
const logD = new DiagnosticsLogger('unifiedAuth')

export const getDeviceIdOrAccessTokenHash = () => {
  if (isInExtension()) {
    return Auth2.getDeviceIdOrAccessTokenHash()
  } else {
    return webGetDeviceIdOrAccessTokenHash()
  }
}

export const getDeviceId = () => {
  if (isInExtension()) {
    return Auth2.getDeviceId()
  } else {
    return webGetDeviceId()
  }
}

export const getAccessToken = () => {
  if (isInExtension()) {
    return Auth2.getAccessToken()
  } else {
    return webGetAccessToken()
  }
}

export const getLastUserId = () => {
  const lastUserId = isInExtension()
    ? Auth2.getLastUserId()
    : getAuthAttribute('lastUserId')

  return lastUserId ?? ''
}

export function getRationing() {
  if (isInExtension()) {
    return Auth2.getRationing()
  } else {
    if (!hasLocalStoragePermission) return {}

    return {
      canMote: localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}canMote`),
      freeMotesMintedEpoch: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}freeMotesMintedEpoch`
      ),
      totalFreeMotes: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}totalFreeMotes`
      ),
      usedFreeMotes: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}usedFreeMotes`
      ),
      availableFreeMotes: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}availableFreeMotes`
      ),
      isUpgradable: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}isUpgradable`
      )
    }
  }
}

export function handleResponseAuthData(response, url) {
  const flattenedData = flattenDataFromResponse(response)
  if (isEmpty(flattenedData)) return

  if (!responseIncludesAuth(flattenedData)) {
    if (isInExtension()) {
      Auth2.updateCacheAndStorage(flattenedData)
    } else {
      _persistDataToStorage(flattenedData)
    }
    return
  }

  const currentAccessToken = getDeviceIdOrAccessTokenHash()?.accessToken

  if (currentAccessToken !== flattenedData.accessToken) {
    logD.info(`Access token changed, url: ${url}`, {
      oldAccessToken: currentAccessToken,
      newAccessToken: flattenedData.accessToken
    })
  }

  if (isInExtension()) {
    Auth2.updateCacheAndStorageFromResponse(flattenedData, url)
  } else {
    _persistBroadcastIdentify(flattenedData, true)
    broadcastLastLoginHostname()
  }
}

export function shouldDebitMote() {
  if (isInExtension()) {
    return Auth2.shouldDebitMote()
  } else {
    if (!hasLocalStoragePermission) return false

    const rationing = reallyTruthy(
      localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}applyFreeMoteRationing`)
    )

    return rationing
  }
}

export function canTrustFreeMotesAllocation() {
  if (isInExtension()) {
    return Auth2.canTrustFreeMotesAllocation()
  } else {
    if (!hasLocalStoragePermission) return false

    const nextReplenishEpoch = localStorage.getItem(
      `${WEB_STORAGE_PREFIX_KEYS}freeMotesMintedEpoch`
    )

    return new Date() < new Date(nextReplenishEpoch * 1000)
  }
}

export const getAuthAttribute = (attribute = '') => {
  if (!hasLocalStoragePermission) return null
  if (!attribute) return null

  return window.localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}${attribute}`)
}

export const forceLogout = () => {
  document.dispatchEvent(
    new CustomEvent('auth:logout', {
      detail: {
        isForced: true,
        isInExtension: isInExtension(),
        error: '403'
      }
    })
  )
}
